import { DataRequestProject } from '../DataRequestProject';
import { DataFormType } from '../DataFormTypeEnum';
import FormNotificationStatus from '../FormNotificationStatus';

export interface FormSeenFlagCall {
    projectId: number;
    formId: number;
}

export interface ViewSeenFlagCall {
    projectId: number;
    tableId: number;
    viewId: number;
}

// For a project the user has never opened (isSeenByUser === false), the backend resets the
// ENTIRE project's seen state on any single seen-flag call — so exactly one call per project
// is enough. These planners replace the old per-form loops that fired one request per form.

export const planFirstOpenQuestionnaireCalls = (
    projects: DataRequestProject[]
): FormSeenFlagCall[] =>
    projects
        .filter((project) => !project.isSeenByUser)
        .flatMap((project) => {
            const firstQuestionnaire = project.dataForms.find(
                (form) => form.formType === DataFormType.Questionnaire
            );

            return firstQuestionnaire
                ? [{ projectId: project.id, formId: firstQuestionnaire.id }]
                : [];
        });

export const planFirstOpenViewCalls = (
    projects: DataRequestProject[]
): ViewSeenFlagCall[] =>
    projects
        .filter((project) => !project.isSeenByUser)
        .flatMap((project) => {
            const tableWithViews = project.dataTables?.find(
                (dataTable) => dataTable?.views?.length
            );
            const firstView = tableWithViews?.views?.find((view) => view);

            return firstView
                ? [
                      {
                          projectId: project.id,
                          tableId: tableWithViews.id,
                          viewId: firstView.id,
                      },
                  ]
                : [];
        });

// Opening a Documents / Financial Data tab marks that form type as seen — one call per
// project, skipped when the local notification state shows there is nothing left to clear
// for this viewer. When no local status is known for a form, the call is kept (conservative).
export const planFormTypeSeenCalls = (
    projects: DataRequestProject[],
    formType: DataFormType,
    formsNotificationsStatus: FormNotificationStatus[]
): FormSeenFlagCall[] =>
    projects.flatMap((project) => {
        const targetForm = project.dataForms.find(
            (form) => form.formType === formType
        );
        if (!targetForm || project.id <= 0 || targetForm.id <= 0) {
            return [];
        }

        const status = formsNotificationsStatus.find(
            (formStatus) => formStatus.formId === targetForm.id
        );
        const hasNothingToClear =
            status &&
            status.notificationsCount === 0 &&
            !status.isNew &&
            !status.isNewProject;

        return hasNothingToClear
            ? []
            : [{ projectId: project.id, formId: targetForm.id }];
    });
